import { getPrimaryEmail } from "@/lib/clientContact";
import { useCallback, useMemo, useRef, useState } from "react";
import { QueryClient, useMutation } from "@tanstack/react-query";
import {
  createParticipant,
  deleteParticipant,
  sendLog,
  updateParticipant,
} from "@/api";
import { PARTICIPANTS_GET } from "@/api/client/participant";
import {
  Client,
  Conversation,
  LogLevel,
  Participant,
  TranscriptSegment,
} from "@/types";
import { ANALYTICS_EVENTS } from "@/constants";
import { showErrorToast } from "@/lib/errorUtils";
import {
  getParticipantName,
  getParticipantEmail,
} from "@/lib/participant-utils";
import { useAnalytics } from "@/lib";
import type { UpdateParticipantData } from "@/components/pages/Conversations/ConversationsDetails/ConversationPersonTag/ConversationPersonPopoverPaginated";

interface Params {
  conversationId: string;
  conversation: Conversation | undefined;
  transcriptMessages: TranscriptSegment[] | undefined;
  queryClient: QueryClient;
  participants?: Participant[];
  onRequestParticipantDeletion: (id: string | null) => void;
}

export const useConversationParticipantMutations = ({
  conversationId,
  conversation,
  transcriptMessages,
  queryClient,
  participants = [],
  onRequestParticipantDeletion,
}: Params) => {
  const { track } = useAnalytics();
  const removalsInFlight = useRef(new Set<string>());
  const [isSpeakerUpdating, setIsSpeakerUpdating] = useState(false);
  const [isSpeakerUpdated, setIsSpeakerUpdated] = useState(false);

  const { mutate: onCreateParticipant, mutateAsync: createParticipantAsync } =
    useMutation({
      mutationFn: (data: Partial<Participant>) => createParticipant(data),
      onSuccess: (data, variables) => {
        // If creating a participant with a speakerId, show reprocess warning
        if (variables.speakerId) {
          setIsSpeakerUpdated(true);
        }
        queryClient.setQueryData(
          [PARTICIPANTS_GET, conversationId],
          (oldData: Participant[] | undefined) => [...(oldData ?? []), data!],
        );
        queryClient.invalidateQueries({
          queryKey: [PARTICIPANTS_GET, conversationId],
        });
      },
      onError: (error) => {
        showErrorToast(error, "conversation");
      },
    });

  const {
    mutateAsync: onUpdateParticipant,
    isPending: isParticipantUpdating,
    variables: updateParticipantVars,
  } = useMutation({
    mutationFn: (data: Partial<Participant>) => updateParticipant(data),
    onSuccess: (data, variables) => {
      const participantsCached = queryClient.getQueryData<Participant[]>([
        PARTICIPANTS_GET,
        conversationId,
      ]);
      const participantCached = participantsCached?.find(
        (p) => p.id === variables.id,
      );

      if (
        participantCached &&
        participantCached?.speakerId !== variables?.speakerId
      ) {
        setIsSpeakerUpdated(true);
      }

      queryClient.setQueryData(
        [PARTICIPANTS_GET, conversationId],
        (oldData: Participant[] | undefined) =>
          (oldData ?? []).map((p) =>
            p.id === data?.id ? (data as Participant) : p,
          ),
      );
      queryClient.invalidateQueries({
        queryKey: [PARTICIPANTS_GET, conversationId],
      });
    },
    onError: (error) => {
      showErrorToast(error, "conversation");
    },
  });

  const {
    mutate: onDeleteParticipant,
    isPending: isParticipantDeleting,
    variables: deleteParticipantId,
  } = useMutation({
    mutationFn: (id: string) => deleteParticipant(id),
    onSuccess: (_data, deletedId) => {
      queryClient.setQueryData(
        [PARTICIPANTS_GET, conversationId],
        (oldData: Participant[] | undefined) =>
          (oldData ?? []).filter((p) => p.id !== deletedId),
      );
      queryClient.invalidateQueries({
        queryKey: [PARTICIPANTS_GET, conversationId],
      });
    },
    onError: (error) => {
      showErrorToast(error, "conversation");
    },
  });

  // First removal clears the identity link; only an already-unlinked card
  // enters the existing delete-confirmation flow. Always read fresh cache data.
  const requestParticipantRemoval = useCallback(
    async (id: string | null) => {
      if (id === null) {
        onRequestParticipantDeletion(null);
        return;
      }
      if (removalsInFlight.current.has(id)) return;
      const current =
        queryClient.getQueryData<Participant[]>([
          PARTICIPANTS_GET,
          conversationId,
        ]) ?? participants;
      const participant = current.find((p) => p.id === id);
      if (!participant) return;
      if (!participant.clientId && !participant.userId) {
        onRequestParticipantDeletion(id);
        return;
      }
      removalsInFlight.current.add(id);
      try {
        await onUpdateParticipant({
          id,
          conversationId,
          speakerId: participant.speakerId,
          name: getParticipantName(participant),
          email: getParticipantEmail(participant) || undefined,
          clientId: null,
          userId: null,
        });
        setIsSpeakerUpdated(true);
      } catch {
        // The mutation displays the error; keep the original card and mapping.
      } finally {
        removalsInFlight.current.delete(id);
      }
    },
    [
      conversationId,
      participants,
      queryClient,
      onUpdateParticipant,
      onRequestParticipantDeletion,
    ],
  );

  const createOrUpdateParticipant = useCallback(
    (data: Partial<Participant>) => {
      if (!data.id) {
        onCreateParticipant(data);
      } else if (data.id) {
        onUpdateParticipant({
          id: data.id,
          ...data,
        });
      }
    },
    [onCreateParticipant, onUpdateParticipant],
  );

  const reassignSpeaker = useCallback(
    async (participantId: string, speakerId: string) => {
      setIsSpeakerUpdating(true);

      // Find the participant that previously had this speakerId (before reassignment)
      const currentParticipants =
        queryClient.getQueryData<Participant[]>([
          PARTICIPANTS_GET,
          conversationId,
        ]) ?? [];
      const oldParticipant = currentParticipants.find(
        (p) => p.speakerId === speakerId,
      );
      const oldParticipantId = oldParticipant?.id ?? null;

      try {
        // Capture the participant's previous speaker BEFORE reassignment.
        // This is the "freshly vacated" speaker — we'll exclude it from
        // auto-link below so we don't surprise the user with a swap.
        const movedParticipantSourceSpeakerId = currentParticipants.find(
          (p) => p.id === participantId,
        )?.speakerId;

        // list of participants with the same speakerId
        const participantsWithSameSpeaker = currentParticipants.filter(
          (p) => p.speakerId === speakerId && p.id !== participantId,
        );

        if (participantsWithSameSpeaker?.length) {
          await Promise.allSettled(
            participantsWithSameSpeaker.map((p) => {
              return updateParticipant({
                id: p.id,
                conversationId: conversationId,
                speakerId: null, // Remove speakerId from other participants
              });
            }),
          );
          queryClient.setQueryData(
            [PARTICIPANTS_GET, conversationId],
            (oldData: Participant[] | undefined) =>
              (oldData ?? []).map((p) => {
                if (p.speakerId === speakerId && p.id !== participantId) {
                  return { ...p, speakerId: null };
                }
                return p;
              }),
          );
        }

        const updatedParticipant = {
          id: participantId,
          conversationId: conversationId,
          speakerId: speakerId || null,
        };

        await onUpdateParticipant(updatedParticipant);

        // After assigning a speaker, check if there's now exactly 1 unassigned
        // speaker and 1 unassigned participant — auto-link via process of elimination.
        // Exclude the speaker the moved participant just left ("freshly vacated"):
        // auto-linking onto it would create a surprising swap. Speakers that were
        // already empty (not vacated by this action) remain eligible.
        if (transcriptMessages?.length) {
          const allParticipants =
            queryClient.getQueryData<Participant[]>([
              PARTICIPANTS_GET,
              conversationId,
            ]) ?? [];

          const transcriptSpeakerIds = new Set(
            transcriptMessages.map((s) => s.speakerId).filter(Boolean),
          );
          const assignedSpeakerIds = new Set(
            allParticipants.filter((p) => p.speakerId).map((p) => p.speakerId),
          );
          // Also count the speaker we just assigned (cache may not have updated yet)
          assignedSpeakerIds.add(speakerId);

          const rawUnassignedSpeakerIds = [...transcriptSpeakerIds].filter(
            (id) => !assignedSpeakerIds.has(id),
          );
          const unassignedParticipants = allParticipants.filter(
            (p) =>
              !p.speakerId &&
              p.id !== participantId &&
              (p.clientId || p.userId),
          );

          // Only fire when there is genuinely ONE unassigned speaker and ONE
          // unassigned participant. If multiple speakers are unassigned, the
          // exclusion would otherwise let us "pick" the non-excluded one —
          // that's guessing, not process-of-elimination.
          if (
            rawUnassignedSpeakerIds.length === 1 &&
            unassignedParticipants.length === 1 &&
            rawUnassignedSpeakerIds[0] !== movedParticipantSourceSpeakerId
          ) {
            try {
              await onUpdateParticipant({
                id: unassignedParticipants[0].id,
                conversationId,
                speakerId: rawUnassignedSpeakerIds[0],
              });
            } catch {
              // Best-effort inferred auto-link — failure must not block the
              // post-assignment side effects for the manual reassignment below.
              // The mutation's onError already surfaces a toast.
            }
          }
        }

        // Track the speaker participant change event
        track(ANALYTICS_EVENTS.SPEAKER_PARTICIPANT_CHANGED, {
          conversationId,
          speakerId,
          oldParticipantId,
          newParticipantId: participantId,
        });
      } catch (error) {
        sendLog({
          level: LogLevel.ERROR,
          message: "Error reassigning speaker",
          component: "ConversationDetailsProvider",
          data: {
            error: (error as Error)?.message || "",
            conversationId,
          },
        });
      } finally {
        setIsSpeakerUpdating(false);
      }
    },
    [
      conversationId,
      onUpdateParticipant,
      queryClient,
      transcriptMessages,
      track,
    ],
  );

  const onSelectParticipant = useCallback(
    ({ data, type, participantId, speakerId }: UpdateParticipantData) => {
      // If speakerId is undefined ("+" button case), try process of elimination
      let resolvedSpeakerId = speakerId;
      if (!resolvedSpeakerId && transcriptMessages?.length) {
        const currentParticipants =
          queryClient.getQueryData<Participant[]>([
            PARTICIPANTS_GET,
            conversationId,
          ]) ?? [];

        // Get unique speaker IDs from transcript
        const transcriptSpeakerIds = new Set(
          transcriptMessages
            .map((segment) => segment.speakerId)
            .filter(Boolean),
        );

        // Get speaker IDs already assigned to participants
        const assignedSpeakerIds = new Set(
          currentParticipants
            .filter((p) => p.speakerId)
            .map((p) => p.speakerId),
        );

        // Find unassigned speakers
        const unassignedSpeakerIds = [...transcriptSpeakerIds].filter(
          (id) => !assignedSpeakerIds.has(id),
        );

        // If exactly 1 unassigned speaker, use process of elimination
        if (unassignedSpeakerIds.length === 1) {
          resolvedSpeakerId = unassignedSpeakerIds[0];
        }
      }

      const dataToUpdate = {
        conversationId: conversationId,
        name: `${data.firstname} ${data.lastname}`,
        email:
          (type === "client" ? getPrimaryEmail(data as Client) : data.email) ||
          null,
        id: participantId,
        speakerId: resolvedSpeakerId,
        clientId: null,
        userId: null,
      };

      if (type === "client") {
        createOrUpdateParticipant({
          ...dataToUpdate,
          clientId: data.id,
        });
      } else {
        createOrUpdateParticipant({
          ...dataToUpdate,
          userId: data.id,
        });
      }
    },
    [
      conversationId,
      createOrUpdateParticipant,
      queryClient,
      transcriptMessages,
    ],
  );

  const closeSpeakerUpdateWarning = useCallback(() => {
    setIsSpeakerUpdated(false);
  }, []);

  const markSpeakerUpdated = useCallback(() => {
    setIsSpeakerUpdated(true);
  }, []);

  const participantUpdatingId = useMemo(() => {
    if (isSpeakerUpdating) {
      return undefined;
    }
    if (
      isParticipantUpdating &&
      (updateParticipantVars?.email || updateParticipantVars?.name)
    ) {
      return updateParticipantVars?.id;
    }
    if (isParticipantDeleting) {
      return deleteParticipantId;
    }
    return undefined;
  }, [
    isSpeakerUpdating,
    isParticipantUpdating,
    updateParticipantVars?.email,
    updateParticipantVars?.name,
    updateParticipantVars?.id,
    isParticipantDeleting,
    deleteParticipantId,
  ]);

  return {
    requestParticipantRemoval,
    createOrUpdateParticipant,
    createParticipantAsync,
    updateParticipantAsync: onUpdateParticipant,
    onDeleteParticipant,
    onSelectParticipant,
    reassignSpeaker,
    isSpeakerUpdating: isSpeakerUpdating || isParticipantUpdating,
    isSpeakerUpdated,
    closeSpeakerUpdateWarning,
    markSpeakerUpdated,
    participantUpdatingId,
  };
};
